import React from 'react';
import { Icon, IconType } from '../icons';
import { TDropdown, BosDropDownOptions, cn } from 'types';

export const Dropdown = (props: TDropdown) => {
  const {
    options,
    value,
    iconProps,
    disabled,
    searchText,
    disableEnter,
    isMulti,
    placeholder,
    className,
    label,
    maxMenuHeight,
    minMenuHeight,
    nextDropdown = false,
    isDropdownWithSearch,
    hideSearchBox,
    customMenuListHeader,
    keepDialogOpenAfterSelection,
    hideSelectionIcon,
    multiIconClassNames,
    menuClassName,
    onChange,
    isDropdownOpen,
    setSearchText,
    hasSearchValue,
  } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const id = props?.id || 'dropdown';
  const defaultValue = options?.find((item) => item?.isSelected) || null;
  const [variant, setVariant] = React.useState(
    iconProps?.iconDropdownDown ? iconProps.iconDropdownDown : IconType.NewNextChevronDown
  );
  const [size, setSize] = React.useState({
    iconHeight: iconProps?.iconHeight ?? 24,
    iconWidth: iconProps?.iconWidth ?? 24,
    iconSvgColor: iconProps?.iconColor ?? '',
  });

  const [internalSearchText, setInternalSearchText] = React.useState('');
  const [multiSearchText, setMultiSearchText] = React.useState('');

  const effectiveSearchText = searchText ?? internalSearchText;

  React.useEffect(() => {
    isDropdownOpen?.(isOpen);
    const iconDown = iconProps?.iconDropdownDown ? iconProps?.iconDropdownDown : IconType.NewNextChevronDown;
    const iconUp = iconProps?.iconDropdownUp ? iconProps?.iconDropdownUp : IconType.NewNextChevronUp;
    setVariant(isOpen ? iconUp : iconDown);

    setSize({
      iconHeight: iconProps?.iconHeight ?? 24,
      iconWidth: iconProps?.iconWidth ?? 24,
      iconSvgColor: iconProps?.iconColor ?? '',
    });
  }, [isOpen]);

  React.useEffect(() => {
    if (defaultValue && !value) {
      onChange?.(defaultValue as any);
    }
  }, []);

  React.useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMultiSearchText('');
    setInternalSearchText('');
    if (setSearchText) {
      setSearchText('');
    }
  }, [isOpen]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const preventInputPropagation = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Backspace') {
      event.stopPropagation();
    }
    if (disableEnter && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const getDisplayValue = () => {
    if (!value) return <span className="truncate">{placeholder}</span>;
    if (Array.isArray(value)) {
      if (value.length === 0)
        return (
          <span className="truncate w-full pl-2">
            {isMulti ? (
              <input
                type="text"
                value={multiSearchText}
                placeholder={placeholder}
                onKeyDown={preventInputPropagation}
                onChange={(e) => {
                  setMultiSearchText(e.target.value);
                  setInternalSearchText(e.target.value);
                  setSearchText?.(e.target.value);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                }}
                className="w-full bg-transparent focus:outline-none p-0 border-none"
              />
            ) : (
              placeholder
            )}
          </span>
        );
      return (
        <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto noScrollbar">
          {value.map((v, i) => (
            <div
              key={i}
              className="flex items-stretch gap-1 bg-neutralGrey rounded text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="flex items-center px-2 py-0.5">{v.label}</span>
              <div
                className="group cursor-pointer flex items-center justify-center h-full w-6 hover:bg-lightDestructive transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const newValue = (value as BosDropDownOptions[]).filter((item) => item.value !== v.value);
                  onChange(newValue as any);
                }}
              >
                <Icon
                  icon={IconType.Close}
                  width={16}
                  height={16}
                  fill="currentColor"
                  className="text-black group-hover:text-poppy"
                />
              </div>
            </div>
          ))}
          {isMulti && (
            <input
              type="text"
              value={multiSearchText}
              onKeyDown={preventInputPropagation}
              onChange={(e) => {
                setMultiSearchText(e.target.value);
                setInternalSearchText(e.target.value);
                setSearchText?.(e.target.value);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(true);
              }}
              className="w-fit bg-transparent focus:outline-none p-0 border-none hidden" // Added hidden class because the input taken space on dropdown selection
            />
          )}
        </div>
      );
    }

    return <span className="truncate w-full">{(value as BosDropDownOptions).label}</span>;
  };

  const filterOptions = (inputValue: string): BosDropDownOptions[] => {
    const selectedValues = isMulti && Array.isArray(value) ? (value as BosDropDownOptions[]) : [];

    const excludeSelected = (opts: BosDropDownOptions[]) =>
      isMulti ? opts.filter((opt) => !selectedValues.some((sel) => sel?.value === opt?.value)) : opts;

    if (isDropdownWithSearch && inputValue) {
      return excludeSelected(
        options.filter((option) => {
          if (typeof option.label === 'string') {
            return option.label?.toLowerCase().includes(inputValue?.toLowerCase());
          }
          return true;
        })
      );
    }
    if (isMulti) {
      return excludeSelected(
        options.filter((option) => {
          if (typeof option.label === 'string') {
            return option.label?.toLowerCase().includes(multiSearchText?.toLowerCase());
          }
          return true;
        })
      );
    }
    return options;
  };

  const handleOptionClick = (option: BosDropDownOptions) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? (value as BosDropDownOptions[]) : [];
      const isAlreadySelected = currentValues.some((v) => v?.value === option?.value);
      let newValues;
      if (isAlreadySelected) {
        newValues = currentValues.filter((v) => v?.value !== option?.value);
      } else {
        newValues = [...currentValues, option];
      }
      onChange?.(newValues as any);
      setMultiSearchText('');
      setInternalSearchText('');
      setSearchText?.('');
    } else {
      onChange?.(option as any);
    }

    if (keepDialogOpenAfterSelection) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    hasSearchValue?.(Boolean(effectiveSearchText));
  }, [effectiveSearchText]);

  return (
    <div className="flex flex-col gap-1 w-full justify-center">
      {label && <div className="text-base font-semibold p-1/2">{label}</div>}
      <div className="relative w-full" id={id} ref={dropdownRef}>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled) setIsOpen(!isOpen);
            }
          }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(
            'flex w-full justify-between items-center py-3 px-4 text-base rounded-lg border border-neutralPlus text-black',
            nextDropdown ? 'font-semibold' : 'font-semibold',
            disabled
              ? 'bg-lightGrey cursor-not-allowed opacity-50 border-neutralGrey text-neutralPlus'
              : 'bg-white cursor-pointer',
            nextDropdown &&
              !disabled &&
              'hover:border-black focus:ring-0 focus:border-black focus:outline focus:outline-black',
            className
          )}
        >
          {getDisplayValue()}
          <div className="cursor-pointer w-fit px-1 flex items-center gap-4">
            {isMulti && Array.isArray(value) && value.length > 0 && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.([] as any);
                }}
              >
                <Icon icon={IconType.Close} width={16} height={16} className={multiIconClassNames} />
              </div>
            )}
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) setIsOpen(!isOpen);
              }}
            >
              <Icon icon={variant} height={size?.iconHeight} width={size?.iconWidth} fill={size?.iconSvgColor} />
            </div>
          </div>
        </div>
        {isOpen && (
          <div
            className={cn(
              'w-full absolute h-fit z-1 mt-2 rounded-lg border border-neutralPlus bg-white',
              nextDropdown
                ? 'shadow-[0px_2px_16px_0px_rgba(48,48,111,0.08),0px_8px_24px_2px_rgba(48,48,111,0.04)]'
                : 'shadow-lg',
              menuClassName
            )}
          >
            {customMenuListHeader ?? null}
            {isDropdownWithSearch && !hideSearchBox && (
              <div
                className={cn('relative flex items-center gap-4 p-3', nextDropdown && 'border-b border-neutralGrey')}
              >
                <div className="absolute left-7 top-1/2 -mt-[12px] w-6 h-6 shrink-0 text-neutralPlus pointer-events-none">
                  <Icon icon={IconType.NewNextSearch} width={24} height={24} />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={effectiveSearchText}
                  onKeyDown={preventInputPropagation}
                  onChange={(e) => {
                    setSearchText?.(e?.target?.value);
                    if (searchText === undefined) {
                      setInternalSearchText(e.target.value);
                    }
                  }}
                  className="w-full py-3 pl-4 pr-12 rounded-lg border border-neutralPlus bg-white indent-7 focus:outline-none focus:ring-1 focus:ring-black"
                />
                {effectiveSearchText && (
                  <div
                    className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center justify-center p-1 rounded-full cursor-pointer transition-colors duration-200 text-black z-[1]"
                    onClick={() => {
                      setSearchText?.('');
                      if (searchText === undefined) {
                        setInternalSearchText('');
                      }
                    }}
                  >
                    <Icon icon={IconType.NewNextCross} width={20} height={20} />
                  </div>
                )}
              </div>
            )}
            <ul
              className={cn(
                'w-full overflow-auto p-0 max-h-[300px]',
                isDropdownWithSearch ? 'rounded-b-lg' : 'rounded-lg',
                customMenuListHeader && 'relative -top-6 -mb-6',
                maxMenuHeight && `max-h-[${maxMenuHeight}px]`,
                minMenuHeight && `min-h-[${minMenuHeight}px]`
              )}
            >
              {filterOptions(effectiveSearchText).length > 0 ? (
                filterOptions(effectiveSearchText).map((option, idx) => {
                  const isSelected =
                    isMulti && Array.isArray(value)
                      ? (value as BosDropDownOptions[])?.some((v) => v?.value === option?.value)
                      : (value as BosDropDownOptions)?.value == option?.value;
                  const isDisabled = option?.isDisabled ?? false;

                  return (
                    <li
                      key={`${idx}-${option?.label}-${option?.value}`}
                      className={cn(
                        'py-3 px-4 text-base/[175%] font-semibold relative flex items-center',
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                        nextDropdown
                          ? cn(
                              'border-b border-neutralGrey last:border-b-0',
                              isDisabled
                                ? 'bg-lightGrey'
                                : isSelected
                                  ? 'bg-concrete text-white'
                                  : 'bg-white hover:bg-lightGrey'
                            )
                          : isDisabled
                            ? 'bg-lightGrey'
                            : isSelected
                              ? 'bg-blue-500 text-white'
                              : 'hover:bg-blue20'
                      )}
                      onClick={() => !isDisabled && handleOptionClick(option)}
                    >
                      {option?.label}
                      {!hideSelectionIcon && nextDropdown && isSelected && !isDisabled && (
                        <div className="absolute right-4">
                          <Icon icon={IconType.Check} width={24} height={24} fill="white" />
                        </div>
                      )}
                    </li>
                  );
                })
              ) : (
                <li className="py-3 px-4 text-base font-semibold text-neutralPlus text-center cursor-default">
                  No options
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
